import React, { useRef, useCallback, useReducer } from 'react';
import { StyleSheet, View, Keyboard, Platform } from 'react-native';
import { router } from 'expo-router';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import FloatingLabelInput from '@/components/Inputs/FloatingLabelInput';
import { API_HOST } from '@/constants/config';
import { useAuthStore } from '@/store';
import Turnstile from '@/components/Turnstile';
import Link from '@/components/Typography/Link';
import SubmitButton from '@/components/Buttons/SubmitButton';
import PropTypes from 'prop-types';
import StatusContainer from '@/components/StatusContainer';
import SSOButtons from '@/components/Auth/SSOButtons';
import { getClientInfo, trackEvent, trackUser } from '@/constants/utils/analytics';

// Move styles outside component to prevent recreation
const styles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: 400,
    gap: 6,
  },
  inputContainer: {
    gap: 8,
  },
  footer: {
    gap: 12,
    marginTop: 24,
    alignItems: 'center',
  },
  footerLinks: {
    flexDirection: 'row',
    gap: 4,
    justifyContent: 'center',
  },
});

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Platform check
const isWeb = Platform.OS === 'web';

// Form validation
const validateEmail = (email, t) => {
  if (!email.trim()) {
    return t('auth.login.errors.emailRequired');
  }
  if (!EMAIL_REGEX.test(email.trim())) {
    return t('auth.login.errors.invalidEmail');
  }
  return null;
};

const validatePassword = (password, t) => {
  if (!password.trim()) {
    return t('auth.login.errors.passwordRequired');
  }
  if (password.length < 8) {
    return t('auth.login.errors.passwordTooShort');
  }
  return null;
};

// Form reducer
const formReducer = (state, action) => {
  switch (action.type) {
    case 'SET_EMAIL':
      return { ...state, email: action.payload, error: '' };
    case 'SET_PASSWORD':
      return { ...state, password: action.payload, error: '' };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'RESET':
      return { ...initialFormState };
    default:
      return state;
  }
};

// Initial form state
const initialFormState = {
  email: '',
  password: '',
  error: '',
  isLoading: false,
};

// Memoized footer links component
const FormLinks = React.memo(({ t }) => (
  <View style={styles.footer}>
    <View style={styles.footerLinks}>
      <Text color='secondary'>{t('auth.login.noAccount')}</Text>
      <Link href="/signup">
        {t('auth.login.signupLink')}
      </Link>
    </View>
    <View style={styles.footerLinks}>
      <Link href="/forgot-password">
        {t('auth.login.forgotPassword')}
      </Link>
    </View>
  </View>
));

FormLinks.propTypes = {
  t: PropTypes.func.isRequired,
};

const LoginForm = ({ onSuccess, showLinks = true }) => {
  const { t } = useTranslation();
  const setAuthData = useAuthStore(state => state.setAuthData);
  const turnstileRef = useRef(null);
  const passwordInputRef = useRef(null);
  
  const [formState, dispatch] = useReducer(formReducer, initialFormState);

  // Memoized input handlers
  const handleEmailChange = useCallback((text) => {
    dispatch({ type: 'SET_EMAIL', payload: text });
  }, []);

  const handlePasswordChange = useCallback((text) => {
    dispatch({ type: 'SET_PASSWORD', payload: text });
  }, []);

  // Validate form
  const validateForm = useCallback(() => {
    const emailError = validateEmail(formState.email, t);
    if (emailError) {
      dispatch({ type: 'SET_ERROR', payload: emailError });
      return false;
    }

    const passwordError = validatePassword(formState.password, t);
    if (passwordError) {
      dispatch({ type: 'SET_ERROR', payload: passwordError });
      return false;
    }

    return true;
  }, [formState.email, formState.password, t]);

  const handleLogin = useCallback(async () => {
    try {
      Keyboard.dismiss();
      
      if (!validateForm()) {
        return;
      }

      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: '' });

      // Get turnstile token on submit (non-blocking until now)
      let turnstileToken = null;
      if (isWeb) {
        try {
          turnstileToken = await turnstileRef.current?.execute();
        } catch (captchaError) {
          dispatch({ type: 'SET_ERROR', payload: t('auth.login.errors.captchaRequired') });
          dispatch({ type: 'SET_LOADING', payload: false });
          return;
        }
      }

      const clientInfo = getClientInfo();

      if (__DEV__ && !isWeb) {
        console.log('[Login] API_HOST:', API_HOST);
      }

      const cleanEmail = formState.email.trim().replace(/[\u00A0\u200B\u200C\u200D\uFEFF]/g, '');
      const cleanPassword = formState.password.replace(/[\u00A0\u200B\u200C\u200D\uFEFF]/g, '').trim();

      const response = await fetch(`${API_HOST}/json/auth/login/email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: cleanEmail, 
          password: cleanPassword,
          ...(isWeb && { turnstileToken }),
          clientInfo,
        }),
      });

      if (__DEV__ && !isWeb) {
        console.log('[Login] Response status:', response.status);
      }

      let data;
      try {
        data = await response.json();
      } catch (_) {
        throw new Error(t('auth.login.errors.loginFailed'));
      }

      if (__DEV__ && !isWeb) {
        console.log('[Login] Response status:', response.status, 'ok:', response.ok);
      }

      if (!response.ok || data.error) {
        const errorMsg = data.message || t('auth.login.errors.loginFailed');
        if (__DEV__ && !isWeb) {
          console.log('[Login] Error:', errorMsg, 'API:', API_HOST, 'Status:', response.status);
        }
        throw new Error(errorMsg);
      }

      // If MFA is required, redirect to MFA screen with the ticket
      if (data.mfaRequired && data.ticket) {
        router.push({ pathname: '/login-authorize', params: { ticket: data.ticket, email: cleanEmail } });
        return;
      }

      // Store auth data when tokens are present (no MFA case)
      if (data.tokens?.access) {
        await setAuthData(data.tokens.access, data.tokens.expiresAt, data.tokens.refresh, data.user);
      }

      if (data.user?.id) trackUser(data.user.id);
      trackEvent('auth', 'login_success', 'email');
      onSuccess?.(data);
      router.replace(Platform.OS === 'web' ? '/' : '/(app)/app');
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message || t('auth.login.errors.loginFailed') });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [formState.email, formState.password, t, onSuccess, setAuthData, validateForm]);

  return (
    <View style={styles.container}>
      <SSOButtons onSuccess={onSuccess} mode="login" />

      <View style={styles.inputContainer}>
        <FloatingLabelInput
          label={t('auth.login.emailLabel')}
          value={formState.email}
          onChangeText={handleEmailChange}
          keyboardType="email-address"
          autoCapitalize="none"
          onSubmitEditing={() => passwordInputRef.current?.focus()}
          returnKeyType="next"
          blurOnSubmit={false}
          autoComplete="email"
          textContentType="emailAddress"
        />

        <FloatingLabelInput
          ref={passwordInputRef}
          label={t('auth.login.passwordLabel')}
          value={formState.password}
          onChangeText={handlePasswordChange}
          secureTextEntry
          onSubmitEditing={handleLogin}
          returnKeyType="go"
          autoComplete="password"
          textContentType="password"
        />
      </View>

      {formState.error ? (
        <StatusContainer type="error" message={formState.error} />
      ) : null}

      {isWeb && <Turnstile ref={turnstileRef} />}

      <SubmitButton
        onPress={handleLogin}
        isLoading={formState.isLoading}
        isDisabled={!formState.email.trim() || !formState.password.trim()}
        title={t('auth.login.submitButton')}
      />

      {showLinks && <FormLinks t={t} />}
    </View>
  );
}

LoginForm.propTypes = {
  onSuccess: PropTypes.func,
  showLinks: PropTypes.bool,
}; 

export default React.memo(LoginForm); 